import { FC, useEffect, useRef } from 'react';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { useBrowserModalControls } from '@telialt/common-hooks/src/useBrowserModalControls';
import { NavigationItem } from '@telialt/models/src/Navigation';
import { isSSR } from '@telialt/utils/src/helpers';
import { useResolution } from '@telialt/utils/src/hooks';
import { isDesktopRes } from '@telialt/utils/src/responsive';

import { MenuDrawer } from '../MenuDrawer/MenuDrawer';
import { MenuItemList } from '../MenuItemList/MenuItemList';
import { MobileMenu } from '../MobileMenu/MobileMenu';
import { BurgerButton } from './BurgerButton';
import { BurgerMenuContainer, ContextMenuContainer, MainMenuContainer, MobileMenuWrapper } from './BurgerMenu.styles';

type Props = {
    items?: NavigationItem[];
    contextItems?: NavigationItem[];
};

export const BurgerMenu: FC<Props> = ({ items, contextItems }) => {
    const { t } = useTranslations();

    const { isModalOpen, openModal, closeModal } = useBrowserModalControls('navigation-mobile-menu');
    const isDesktop = useResolution(() => !isSSR() && isDesktopRes());

    const menuButtonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (isDesktop && isModalOpen) {
            closeModal();
        }
    }, [closeModal, isDesktop, isModalOpen]);

    const handleToggleDrawer = () => {
        if (isModalOpen) {
            closeModal();
        } else {
            openModal();
        }
    };

    if (!items?.length && !contextItems?.length) {
        return null;
    }

    const menuId = 'mobile-menu';

    return (
        <BurgerMenuContainer>
            <BurgerButton ref={menuButtonRef} menuId={menuId} isOpen={isModalOpen} onOpenToggle={handleToggleDrawer} />
            <MenuDrawer id={menuId} buttonRef={menuButtonRef} isVisible={isModalOpen} onClose={() => closeModal()}>
                <MobileMenuWrapper role="group" aria-label={t['navbar.burger.aria_label']}>
                    <MainMenuContainer>
                        <MobileMenu items={items} isVisible={isModalOpen} />
                    </MainMenuContainer>
                    {contextItems?.length > 0 && (
                        <ContextMenuContainer>
                            <MenuItemList items={contextItems} data-section="Other" />
                        </ContextMenuContainer>
                    )}
                </MobileMenuWrapper>
            </MenuDrawer>
        </BurgerMenuContainer>
    );
};
